'use client';

import { useState, useCallback, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger
} from '../dialog/dialog';
import { ModalOverlay, Modal, Dialog as AriaDialog } from '../modals/modal';
import { CloseButton } from '../../base/buttons/close-button';
import { ShareButton } from '../../base/buttons/share-button';
import { Button } from '../../base/buttons/button';
import { Copy01 } from '@untitledui/icons';
import { cx } from '../../../utils/cx';

type CopyStatus = 'idle' | 'copied' | 'error';

export type ShareDialogProps = {
  shareUrl: string;
  title?: string;
  description?: string;
  messageLabel?: string;
  errorStatusMessage?: string;
  copyButtonLabel?: string;
  copiedButtonLabel?: string;
  triggerAriaLabel?: string;
  trigger?: ReactNode;
  className?: string;
  inputClassName?: string;
  /** When true, uses React Aria modal instead of Radix Dialog (for use inside other React Aria modals) */
  useReactAria?: boolean;
  /** Controlled open state (required when useReactAria is true) */
  isOpen?: boolean;
  /** Callback when open state changes (required when useReactAria is true) */
  onOpenChange?: (open: boolean) => void;
};

const DEFAULT_TITLE = 'Share';
const DEFAULT_DESCRIPTION =
  'Encourage your friends to join you in volunteering.';
const DEFAULT_MESSAGE_LABEL = 'Message';
const DEFAULT_ERROR_STATUS = 'Unable to copy link. Please copy it manually.';
const DEFAULT_COPY_LABEL = 'Copy';
const DEFAULT_COPIED_LABEL = 'Copied';

export const ShareDialog = ({
  shareUrl,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  messageLabel = DEFAULT_MESSAGE_LABEL,
  errorStatusMessage = DEFAULT_ERROR_STATUS,
  copyButtonLabel = DEFAULT_COPY_LABEL,
  copiedButtonLabel = DEFAULT_COPIED_LABEL,
  triggerAriaLabel = 'Share',
  trigger,
  className,
  inputClassName,
  useReactAria = false,
  isOpen,
  onOpenChange
}: ShareDialogProps) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setCopyStatus('idle');
      }
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const handleCopyShareLink = async () => {
    try {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(shareUrl);
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopyStatus('copied');
    } catch (_error) {
      setCopyStatus('error');
    }
  };

  const triggerNode = trigger ?? <ShareButton aria-label={triggerAriaLabel} />;
  const errorMessage = copyStatus === 'error' ? errorStatusMessage : '';

  const dialogContent = (
    <>
      <div className="flex flex-col gap-2 p-6">
        <p className="text-sm font-medium tracking-wider text-gray-500">
          {messageLabel}
        </p>
        <div className="flex items-stretch">
          <input
            type="text"
            value={shareUrl}
            readOnly
            aria-label="Share link"
            className={cx(
              'min-h-11 flex-1 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800 focus-visible:z-10 focus-visible:border-gray-400 focus-visible:outline-none',
              inputClassName
            )}
          />
          <Button
            type="button"
            color="secondary"
            size="md"
            aria-label="Copy link"
            onClick={handleCopyShareLink}
            iconLeading={Copy01}
            className="min-h-11 rounded-l-none border-l-0"
          >
            {copyStatus === 'copied' ? copiedButtonLabel : copyButtonLabel}
          </Button>
        </div>
        {errorMessage && (
          <p
            className="min-h-[1rem] text-xs text-[#B42318]"
            role="alert"
            aria-live="polite"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </>
  );

  if (useReactAria) {
    return (
      <ModalOverlay
        className="z-[2000] max-[34em]:items-center"
        isDismissable
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <Modal>
          <AriaDialog>
            <div
              className={cx(
                'w-full max-w-lg rounded-2xl bg-white shadow-xl',
                className
              )}
            >
              <div className="flex items-start justify-between border-b border-gray-200 px-6 pb-4 pt-6">
                <div className="flex flex-col gap-1">
                  <h2 className="m-0 text-base font-semibold text-gray-900">
                    {title}
                  </h2>
                  <p className="m-0 text-sm text-gray-500">{description}</p>
                </div>
                <CloseButton
                  size="lg"
                  onClick={() => handleOpenChange(false)}
                />
              </div>
              {dialogContent}
            </div>
          </AriaDialog>
        </Modal>
      </ModalOverlay>
    );
  }

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{triggerNode}</DialogTrigger>
      <DialogContent
        className={cx('max-w-lg rounded-2xl border-none p-0', className)}
      >
        <DialogHeader className="border-b border-gray-200 px-6 pb-4 pt-6">
          <DialogTitle className="text-md font-semibold text-gray-900">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {description}
          </DialogDescription>
        </DialogHeader>
        {dialogContent}
      </DialogContent>
    </Dialog>
  );
};
